/**
 * Contrato do Assistente (chat que comanda o painel) entre client e server.
 *
 * O stream é SSE (`POST /api/assistente/stream`): cada linha `data:` é um
 * `AssistenteEvento` em JSON. O client acumula `texto`, mostra as tools em
 * andamento e renderiza `acoes`/`confirmacao` como botões.
 */

/** Botão que o assistente oferece ao usuário. */
export type AssistenteAcao =
  | { tipo: "abrir"; rotulo: string; path: string }
  | { tipo: "enviar"; rotulo: string; texto: string }
  | { tipo: "link"; rotulo: string; url: string };

/** Pedido de confirmação de uma tool destrutiva, gravado na mensagem. */
export type AssistentePendencia = {
  toolCallId: string;
  nome: string;
  titulo: string;
  args: Record<string, unknown>;
  /**
   * O que de fato vai acontecer, em texto legível. Sem isto o cartão mostrava
   * os argumentos crus ("templateId: 1"), que não dizem ao usuário o que ele
   * está aprovando.
   */
  campos?: Array<{ rotulo: string; valor: string }>;
  /** Tela para conferir o conteúdo inteiro antes de confirmar. */
  revisarPath?: string;
};

export type AssistenteEvento =
  | { tipo: "inicio"; conversationId: number; userMessageId: number }
  | { tipo: "texto"; delta: string }
  /**
   * O que o assistente está fazendo agora. Existe porque o modelo passa
   * segundos em silêncio entre uma ferramenta e a próxima frase, e sem isto a
   * tela parecia travada.
   */
  | { tipo: "etapa"; texto: string }
  | { tipo: "ferramenta_inicio"; id: string; nome: string; titulo: string }
  | { tipo: "ferramenta_fim"; id: string; ok: boolean; resumo: string }
  | { tipo: "navegar"; path: string }
  /**
   * O assistente mudou dado no sistema. A tela que o usuário está olhando
   * precisa recarregar — senão ele vê o chat dizer "criei" e a lista atrás
   * continuar sem o item, o que parece defeito.
   */
  | { tipo: "dados"; escopo: string }
  /** Executar uma ação que a tela registrou (preencher campo, trocar assunto). */
  | { tipo: "acao_tela"; id: string; acaoId: string; args: Record<string, unknown> }
  | { tipo: "acoes"; acoes: AssistenteAcao[] }
  | { tipo: "confirmacao"; pendencia: AssistentePendencia; messageId: number }
  | { tipo: "fim"; assistantMessageId: number; titulo?: string }
  | { tipo: "erro"; mensagem: string };

/** Corpo do POST /api/assistente/stream. */
export type AssistenteStreamPedido = {
  conversationId?: number;
  /** Texto digitado (ou transcrito). Pode vir vazio se há anexos. */
  texto?: string;
  anexoIds?: number[];
  /** Rota atual do painel, para o modelo saber onde o usuário está. */
  telaAtual?: string;
  /** Área do sistema escolhida no botão "+" (ver shared/assistente-modulos). */
  modulo?: string;
  /** O que a tela aberta está mostrando agora (ver shared/assistente-contexto). */
  contexto?: unknown;
  /** Retomada de uma confirmação pendente. */
  retomar?: { messageId: number; toolCallId: string; aprovado: boolean };
};

export const ASSISTENTE_STREAM_PATH = "/api/assistente/stream";
/** Para onde o navegador conta o que aconteceu ao executar uma ação de tela. */
export const ASSISTENTE_ACAO_PATH = "/api/assistente/acao-resultado";

/** Limites compartilhados (validados nos dois lados). */
export const ASSISTENTE_LIMITES = {
  textoMaxChars: 8_000,
  anexosPorMensagem: 5,
  anexoMaxBytes: 15 * 1024 * 1024,
  imagemMaxBytes: 5 * 1024 * 1024,
  audioMaxBytes: 25 * 1024 * 1024,
} as const;

export const ASSISTENTE_MIMES_ACEITOS = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
  "text/csv",
  "text/markdown",
  "application/json",
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
  "audio/webm",
  "audio/ogg",
  "audio/mpeg",
  "audio/mp4",
  "audio/wav",
  "audio/x-m4a",
] as const;
